import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ScreenRecorder.css';

// Screen recording for a ticket. The video goes straight from the browser to Matix Shot's storage
// (the backend only hands out a one-time upload form), so it is not bound by the attachment limit.
// Flow: setup (mic on/off) → recording (small bar on the page + a floating window that stays on top
// in Chrome/Edge) → review (preview, upload with progress) → onRecorded({ id, shareUrl, size, previewUrl }).
//
// ⚠ The SAME file lives in the customer portal (helpdesk-portal/src/components) and the agents app
// (frontend/src/components). Keep them identical: each app passes its own `reserve` call.
//   reserve({ content_type, size, title }) → Promise<{ id, upload: { url, fields }, shareUrl }>

const MIME_TYPES = [
    // MP4 plays everywhere, iPhone included; Chrome 126+ and Safari can record it.
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
];
const MAX_DURATION_MS = 10 * 60 * 1000;
// Some storage addresses never answer from some networks; a stalled upload is retried on a fresh connection.
const STALL_MS = 15000;
const UPLOAD_ATTEMPTS = 4;

const TEXT = {
    ar: {
        title: 'تسجيل فيديو للشاشة',
        subtitle: 'اشرح المشكلة بالصوت والصورة. سيُرفق الفيديو بالتذكرة تلقائياً.',
        mic: 'تسجيل صوتي من الميكروفون',
        start: 'ابدأ التسجيل',
        pickHint: 'سيطلب المتصفح اختيار الشاشة أو النافذة أو التبويب.',
        cancel: 'إلغاء',
        recording: 'جارٍ التسجيل',
        paused: 'متوقف مؤقتاً',
        pause: 'إيقاف مؤقت',
        resume: 'استئناف',
        stop: 'إنهاء',
        discard: 'حذف',
        review: 'مراجعة التسجيل',
        attach: 'إرفاق بالتذكرة',
        retake: 'إعادة التسجيل',
        uploading: 'جارٍ رفع الفيديو…',
        uploadFailed: 'تعذّر رفع الفيديو. تحقق من الاتصال وحاول مرة أخرى.',
        retry: 'إعادة المحاولة',
        noMic: 'يتم التسجيل بدون ميكروفون (لم يُسمح بالوصول إليه).',
        permissionDenied: 'تم إلغاء اختيار الشاشة.',
        failed: 'تعذّر بدء التسجيل',
        tooLong: 'تم إيقاف التسجيل تلقائياً بعد 10 دقائق.',
        tooBig: 'الفيديو أكبر من الحد المسموح. سجّل فيديو أقصر.',
    },
    en: {
        title: 'Record your screen',
        subtitle: 'Show the problem with your voice. The video is attached to the ticket automatically.',
        mic: 'Record my microphone',
        start: 'Start recording',
        pickHint: 'Your browser will ask which screen, window or tab to record.',
        cancel: 'Cancel',
        recording: 'Recording',
        paused: 'Paused',
        pause: 'Pause',
        resume: 'Resume',
        stop: 'Finish',
        discard: 'Discard',
        review: 'Review the recording',
        attach: 'Attach to ticket',
        retake: 'Record again',
        uploading: 'Uploading the video…',
        uploadFailed: 'The video could not be uploaded. Check your connection and try again.',
        retry: 'Try again',
        noMic: 'Recording without microphone (access was not allowed).',
        permissionDenied: 'Screen selection was cancelled.',
        failed: 'Could not start recording',
        tooLong: 'Recording stopped automatically after 10 minutes.',
        tooBig: 'The video is larger than allowed. Please record a shorter one.',
    },
};

export const canRecordScreen = () =>
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getDisplayMedia &&
    typeof window.MediaRecorder !== 'undefined';

const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const ScreenRecorder = ({ reserve, onRecorded, onClose, language = 'en', title = '', maxBytes = 300 * 1024 * 1024 }) => {
    const t = TEXT[language] || TEXT.en;
    const [phase, setPhase] = useState('setup'); // setup | recording | review
    const [withMic, setWithMic] = useState(true);
    const [paused, setPaused] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');
    const [video, setVideo] = useState(null); // { blob, url }
    const [progress, setProgress] = useState(null); // null | 0..1

    const rec = useRef(null); // { recorder, chunks, cleanup, mime }
    const clock = useRef({ before: 0, since: 0 });
    const pip = useRef(null);
    const ticker = useRef(0);

    const currentElapsed = () =>
        clock.current.before + (rec.current?.recorder.state === 'recording' ? Date.now() - clock.current.since : 0);

    const closePip = () => {
        try { pip.current?.close(); } catch (e) { /* already closed */ }
        pip.current = null;
    };

    const finish = useCallback((discard = false) => {
        const r = rec.current;
        if (!r) return;
        rec.current = null;
        clearInterval(ticker.current);
        closePip();
        const done = () => {
            r.cleanup();
            if (discard) return;
            const blob = new Blob(r.chunks, { type: r.mime.split(';')[0] });
            setVideo({ blob, url: URL.createObjectURL(blob) });
            setPhase('review');
        };
        if (r.recorder.state === 'inactive') done();
        else {
            r.recorder.onstop = done;
            r.recorder.stop();
        }
    }, []);

    // Leaving the page or closing the dialog mid-recording releases the screen and mic.
    useEffect(() => () => finish(true), [finish]);
    useEffect(() => () => video && URL.revokeObjectURL(video.url), [video]);

    const togglePause = useCallback(() => {
        const r = rec.current?.recorder;
        if (!r) return;
        if (r.state === 'recording') {
            r.pause();
            clock.current.before += Date.now() - clock.current.since;
            setPaused(true);
        } else if (r.state === 'paused') {
            r.resume();
            clock.current.since = Date.now();
            setPaused(false);
        }
    }, []);

    // A small always-on-top window (Chrome/Edge 116+) so the controls stay reachable on other tabs and apps.
    const openPip = async () => {
        if (!window.documentPictureInPicture) return null;
        try {
            const win = await window.documentPictureInPicture.requestWindow({ width: 300, height: 56 });
            win.document.title = t.recording;
            win.document.body.innerHTML = `
                <style>
                    body { margin: 0; height: 100vh; display: flex; align-items: center; gap: 8px; padding: 0 10px;
                        background: #1a1a2e; color: #fff; font: 600 14px system-ui, sans-serif; direction: ${language === 'ar' ? 'rtl' : 'ltr'}; }
                    .dot { width: 10px; height: 10px; border-radius: 50%; background: #ef4444; animation: b 1.2s infinite; }
                    .paused .dot { background: #9ca3af; animation: none; }
                    @keyframes b { 50% { opacity: .25; } }
                    .time { font-variant-numeric: tabular-nums; min-width: 48px; }
                    button { border: 0; border-radius: 8px; padding: 8px 12px; font: 600 13px system-ui, sans-serif; cursor: pointer;
                        background: rgba(255,255,255,.12); color: #fff; }
                    button.stop { background: #ef4444; margin-inline-start: auto; }
                </style>
                <span class="dot"></span><span class="time">00:00</span>
                <button class="pause"></button><button class="stop">■ ${t.stop}</button>`;
            win.document.querySelector('.pause').onclick = togglePause;
            win.document.querySelector('.stop').onclick = () => finish();
            win.addEventListener('pagehide', () => { pip.current = null; });
            return win;
        } catch (e) {
            return null;
        }
    };

    const start = async () => {
        setError('');
        setNotice('');
        // Chrome only allows the floating window straight after the click, before the screen picker.
        pip.current = await openPip();
        let display;
        try {
            display = await navigator.mediaDevices.getDisplayMedia({
                video: { frameRate: 30, width: { max: 1920 }, height: { max: 1080 } },
                audio: true, // the tab's or system's sound, when the user ticks "share audio"
            });
        } catch (err) {
            closePip();
            setError(err?.name === 'NotAllowedError' ? t.permissionDenied : `${t.failed}: ${err?.message || err}`);
            return;
        }

        const cleanup = [() => display.getTracks().forEach((tr) => tr.stop())];
        let mic = null;
        if (withMic) {
            try {
                mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
                cleanup.push(() => mic.getTracks().forEach((tr) => tr.stop()));
            } catch (e) {
                setNotice(t.noMic);
            }
        }

        // MediaRecorder takes one audio track, so the shared sound and the mic are mixed into one.
        let audioTracks = [];
        if (display.getAudioTracks().length || mic) {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            cleanup.push(() => ctx.close());
            const dest = ctx.createMediaStreamDestination();
            if (display.getAudioTracks().length) ctx.createMediaStreamSource(display).connect(dest);
            if (mic) ctx.createMediaStreamSource(mic).connect(dest);
            audioTracks = dest.stream.getAudioTracks();
        }

        const mime = MIME_TYPES.find((m) => MediaRecorder.isTypeSupported(m)) || 'video/webm';
        const recorder = new MediaRecorder(new MediaStream([...display.getVideoTracks(), ...audioTracks]), {
            mimeType: mime,
            videoBitsPerSecond: 2500000, // sharp at 1080p for screen content, about 19 MB a minute
        });
        const chunks = [];
        recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        recorder.start(1000);
        rec.current = { recorder, chunks, mime, cleanup: () => cleanup.forEach((fn) => fn()) };
        // The browser's own "Stop sharing" button ends the recording too.
        display.getVideoTracks()[0].addEventListener('ended', () => finish());

        clock.current = { before: 0, since: Date.now() };
        setElapsed(0);
        setPaused(false);
        setPhase('recording');
        ticker.current = setInterval(() => {
            const ms = currentElapsed();
            setElapsed(ms);
            const win = pip.current;
            if (win) {
                const isPaused = rec.current?.recorder.state === 'paused';
                win.document.body.classList.toggle('paused', isPaused);
                win.document.querySelector('.time').textContent = formatTime(ms);
                win.document.querySelector('.pause').textContent = isPaused ? `● ${t.resume}` : `❚❚ ${t.pause}`;
            }
            if (ms >= MAX_DURATION_MS) {
                setNotice(t.tooLong);
                finish();
            }
        }, 250);
    };

    const upload = async () => {
        if (!video) return;
        setError('');
        if (video.blob.size > maxBytes) return setError(t.tooBig);
        setProgress(0);
        try {
            const { id, upload: target, shareUrl } = await reserve({ content_type: video.blob.type, size: video.blob.size, title });
            await sendWithRetry(target, video.blob, setProgress);
            onRecorded({ id, shareUrl, size: video.blob.size, previewUrl: URL.createObjectURL(video.blob), type: video.blob.type });
            onClose();
        } catch (err) {
            setProgress(null);
            setError(err?.response?.data?.message || err?.serverMessage || t.uploadFailed);
        }
    };

    const retake = () => {
        setVideo(null);
        setProgress(null);
        setError('');
        setPhase('setup');
    };

    if (phase === 'recording') {
        return (
            <div className={`sr-bar ${paused ? 'paused' : ''}`} dir={language === 'ar' ? 'rtl' : 'ltr'} role="toolbar" aria-label={t.recording}>
                <span className="sr-dot" />
                <span className="sr-label">{paused ? t.paused : t.recording}</span>
                <span className="sr-time">{formatTime(elapsed)}</span>
                <button type="button" className="sr-btn" onClick={togglePause}>{paused ? `● ${t.resume}` : `❚❚ ${t.pause}`}</button>
                <button type="button" className="sr-btn sr-stop" onClick={() => finish()}>■ {t.stop}</button>
                <button type="button" className="sr-btn sr-discard" onClick={() => { finish(true); onClose(); }} title={t.discard}>✕</button>
            </div>
        );
    }

    const uploading = progress !== null;
    return (
        <div className="sr-overlay" onClick={uploading ? undefined : onClose}>
            <div className="sr-modal" onClick={(e) => e.stopPropagation()} dir={language === 'ar' ? 'rtl' : 'ltr'}>
                <div className="sr-header">
                    <h2>{phase === 'review' ? t.review : t.title}</h2>
                    {phase === 'setup' && <p>{t.subtitle}</p>}
                </div>
                {error && <div className="sr-error">{error}</div>}
                {notice && <div className="sr-notice">{notice}</div>}

                {phase === 'setup' && (
                    <div className="sr-setup">
                        <label className="sr-mic">
                            <input type="checkbox" checked={withMic} onChange={(e) => setWithMic(e.target.checked)} />
                            🎙️ {t.mic}
                        </label>
                        <button type="button" className="sr-start" onClick={start}>⏺ {t.start}</button>
                        <p className="sr-hint">{t.pickHint}</p>
                    </div>
                )}

                {phase === 'review' && video && (
                    <div className="sr-review">
                        <video src={video.url} controls playsInline className="sr-video" />
                        {uploading && (
                            <div className="sr-progress">
                                <div className="sr-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
                                <span>{t.uploading} {Math.round(progress * 100)}%</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="sr-footer">
                    <button type="button" className="sr-fbtn" onClick={onClose} disabled={uploading}>{t.cancel}</button>
                    {phase === 'review' && (
                        <>
                            <button type="button" className="sr-fbtn" onClick={retake} disabled={uploading}>{t.retake}</button>
                            <button type="button" className="sr-fbtn sr-fbtn-primary" onClick={upload} disabled={uploading}>
                                {uploading ? '⏳' : '📎'} {error ? t.retry : t.attach}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

async function sendWithRetry(target, blob, onProgress) {
    for (let attempt = 1; ; attempt++) {
        try {
            return await sendOnce(target, blob, onProgress);
        } catch (err) {
            if (!err.retryable || attempt >= UPLOAD_ATTEMPTS) throw err;
            onProgress(0);
        }
    }
}

function sendOnce(target, blob, onProgress) {
    return new Promise((resolve, reject) => {
        // XHR rather than fetch: it reports upload progress.
        const xhr = new XMLHttpRequest();
        xhr.open('POST', target.url);
        let stall = 0;
        const watch = () => {
            clearTimeout(stall);
            stall = setTimeout(() => xhr.abort(), STALL_MS);
        };
        const fail = (message, retryable) => {
            clearTimeout(stall);
            reject(Object.assign(new Error(message), { retryable }));
        };
        xhr.upload.onprogress = (e) => {
            watch();
            if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
        xhr.onload = () => {
            clearTimeout(stall);
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else fail(`Upload failed (${xhr.status})`, xhr.status >= 500);
        };
        xhr.onerror = () => fail('Upload failed: network error', true);
        xhr.onabort = () => fail('Upload stalled', true);
        watch();
        const form = new FormData();
        Object.entries(target.fields || {}).forEach(([k, v]) => form.append(k, v));
        form.append('file', blob); // the storage service requires the file to be the last field
        xhr.send(form);
    });
}

export default ScreenRecorder;
